import { useCallback, useEffect, useState } from 'react';
import { interviewApi } from 'api';
import { VacancyInterviewStats } from 'types/vacancyStats';

export type StatsErrorKind = 'forbidden' | 'notFound' | 'generic';

export interface StatsError {
  kind: StatsErrorKind;
  message: string;
}

interface UseVacancyStatsResult {
  data: VacancyInterviewStats | null;
  loading: boolean;
  error: StatsError | null;
  refetch: () => void;
}

const mapError = (err: any): StatsError => {
  const status = err?.response?.status;
  if (status === 403) {
    return { kind: 'forbidden', message: 'Нет доступа к статистике этой вакансии.' };
  }
  if (status === 404) {
    return { kind: 'notFound', message: 'Вакансия не найдена.' };
  }
  return { kind: 'generic', message: 'Не удалось загрузить статистику.' };
};

/**
 * Тянет аналитику по вакансии и разруливает состояния loading / error (403/404/прочее).
 * `refetch` используется кнопкой «Повторить».
 */
export function useVacancyStats(vacancyId?: string): UseVacancyStatsResult {
  const [data, setData] = useState<VacancyInterviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<StatsError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    let active = true;

    if (!vacancyId) {
      setData(null);
      setError({ kind: 'notFound', message: 'Вакансия не указана.' });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    interviewApi
      .getVacancyStats(vacancyId)
      .then((stats) => {
        if (active) setData(stats);
      })
      .catch((err) => {
        if (active) {
          setData(null);
          setError(mapError(err));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [vacancyId, reloadKey]);

  return { data, loading, error, refetch };
}
